import { useCallback, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { AddInsuranceSchema, addInsuranceValidationSchema, formInitialValues } from './config';
import { InsuranceFindTypes } from '../../constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { ICustomersInsuranceManualResponse } from '@/shared/endpoints/customers/models/response';
import { CustomersController } from '@/shared/endpoints/customers';
import { ICustomersCreateInsuranceRequest } from '@/shared/endpoints/customers/models/request';
import { Alert, Button, Input, Loading } from '@/shared/ui';
import { acceptNumeric } from '@/shared/helpers/input';
import { toast } from 'react-toastify';
import { IErrorResponse } from '@/shared/types/interfaces';
import { useMediaQuery, useTheme } from '@mui/material';
import { findAllEligibleInsurance } from '@/shared/helpers/eligibility';

export interface IInsuranceManualFormProps {
    className?: string;
    hasInsurance?: boolean;
    onSuccess?(insurance: ICustomersInsuranceManualResponse | null): void;
    onBack?(otherFindType?: InsuranceFindTypes): void;
    onHintField?(fieldName: string): void;
    showLoader?: boolean;
    dismissButtonVisible?: boolean;
}

export const InsuranceManualForm = ({
    className = '',
    onHintField,
    hasInsurance,
    onSuccess,
    onBack,
    showLoader = false,
    dismissButtonVisible = true
}: IInsuranceManualFormProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const onSubmit = useCallback(
        async (form: AddInsuranceSchema, { resetForm }: FormikHelpers<AddInsuranceSchema>) => {
            if (loading) return;
            setLoading(true);
            const result = await dispatch(CustomersController.insuranceCreate({ ...form, coverageType: 'PRIMARY' } as ICustomersCreateInsuranceRequest));

            if (result.meta.requestStatus === 'rejected') {
                toast.error((result.payload as IErrorResponse).message);
                resetForm();
            } else if (result.payload) {
                const insurance = result.payload as ICustomersInsuranceManualResponse;
                onSuccess?.(insurance);
            }
            setLoading(false);
        },
        [dispatch, loading, onSuccess]
    );

    return (
        <div className={`${className || 'w-[528px] 3xl:w-[540px]'} form-container max-w-[100%]`}>
            {loading && showLoader && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark background with some transparency
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                >
                    <Loading />
                </div>
            )}
            <Formik<AddInsuranceSchema>
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={onSubmit}
                initialValues={formInitialValues}
                validationSchema={addInsuranceValidationSchema}
            >
                {({ handleSubmit, setFieldValue, values, errors }) => {
                    return (
                        <Form
                            className="form-wrapper flex flex-col items-center"
                            onSubmit={handleSubmit}
                            data-testid="insurance-manual-form"
                        >
                            <div className="mb-[1.5625rem] w-full">
                                <Input
                                    className="manual_form__id_number"
                                    type="text"
                                    required={true}
                                    inputProps={{ maxLength: 20 }}
                                    label="ID Number"
                                    labelClassName="font-dmSans"
                                    placeholder="ex. 1234567"
                                    value={values.cardHolderID}
                                    subLabel="Sometimes labeled Member ID or Enrollee ID"
                                    onChange={event => setFieldValue('cardHolderID', event.target.value)}
                                    error={!!errors.cardHolderID}
                                    autoFocus={!isMobile}
                                    onFocus={() => onHintField?.('cardHolderID')}
                                    onBlur={() => onHintField?.('')}
                                    testId="insurance-manual-form-card-holder-id"
                                />
                            </div>
                            <div className="mb-[1.5625rem] flex w-full justify-between gap-2">
                                <div className="w-full sm-max:w-[45%]">
                                    <Input
                                        className="manual_form__rx_bin"
                                        type="text"
                                        required={true}
                                        label="BIN"
                                        labelClassName="font-dmSans"
                                        inputProps={{ maxLength: 6 }}
                                        placeholder="ex. 123456"
                                        value={values.provider.bin}
                                        onChange={acceptNumeric(event => setFieldValue('provider.bin', event.target.value))}
                                        error={!!errors.provider?.bin}
                                        onFocus={() => onHintField?.('provider.bin')}
                                        // onBlur={() => onBinPcnBlur(values, setValues, errors, resetForm)}
                                        errorMessage="BIN should have 6 digits"
                                        testId="insurance-manual-form-bin"
                                    />
                                </div>
                                <div className="w-full sm-max:w-[55%]">
                                    <Input
                                        className="manual_form__rx_pcn"
                                        type="text"
                                        required={true}
                                        label="Rx PCN"
                                        data-testid="rxPcnInput"
                                        labelClassName="font-dmSans"
                                        inputProps={{ maxLength: 10 }}
                                        placeholder="ex. 1234567765"
                                        value={values.provider.pcn}
                                        onChange={event => setFieldValue('provider.pcn', event.target.value)}
                                        error={!!errors.provider?.pcn}
                                        onFocus={() => onHintField?.('provider.pcn')}
                                        testId="insurance-manual-form-pcn"
                                    // onBlur={() => onBinPcnBlur(values, setValues, errors, resetForm)}
                                    />
                                </div>
                            </div>
                            <div className="mb-[2.1875rem] w-full">
                                <Input
                                    className="manual_form__rx_group"
                                    type="text"
                                    label="Rx Group"
                                    data-testid="rxGrInput"
                                    labelClassName="font-dmSans"
                                    inputProps={{ maxLength: 15 }}
                                    placeholder="ex. 1234567765"
                                    value={values.groupID}
                                    onChange={event => setFieldValue('groupID', event.target.value)}
                                    error={!!errors.groupID}
                                    subLabel="Sometimes labeled RxGrp"
                                    onFocus={() => onHintField?.('groupID')}
                                    onBlur={() => onHintField?.('')}
                                    testId="insurance-manual-group-id"
                                />
                            </div>
                            {(errors.provider?.pcn || errors.cardHolderID || errors.groupID) && (
                                <div className="mb-[2.5rem] w-full">
                                    <Alert label="Please complete all fields to proceed." />
                                </div>
                            )}
                            <Button
                                className="insurance__add_insurance_btn"
                                type="submit"
                                sx={{ padding: '1rem', width: '100%', marginBottom: '1.25rem', fontSize: '1.125rem' }}
                                showEndIcon
                                loading={loading}
                                testId="button-insurance-manual-add"
                            >
                                <span>Add Insurance</span>
                            </Button>
                            <Button
                                className="insurance__upload_image_btn"
                                variant="outlined"
                                sx={{ padding: '1rem', width: '100%', fontSize: '1.125rem' }}
                                onClick={() => onBack?.('UPLOAD')}
                                testId="button-insurance-manual-switch-to-upload"
                            >
                                <span>
                                    <i className="icon-camera text-5 mr-[0.625rem]" /> Upload Image Instead
                                </span>
                            </Button>

                            {dismissButtonVisible && (
                                <Button
                                    className="insurance__actions_btn"
                                    variant="text"
                                    sx={{ marginTop: '1.25rem', fontSize: '1.125rem' }}
                                    onClick={() => onBack?.()}
                                    testId="button-insurance-manual-back"
                                >
                                    <span>{hasInsurance ? 'Back' : 'Continue without insurance'}</span>
                                </Button>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
